import styled from "styled-components";

import { Table } from "@similarweb/pro-logic-components";
import { colorsPalettes, setFontVariant } from "@similarweb/styles";
import { OnOffSwitch } from "@similarweb/ui-components";
import { Flex } from "@similarweb/ui-components/foundations";

import { swSettings } from "app/common/services/swSettings";
import { GapMultiSelectDropdown } from "app/components/GapMultiSelectDropdown/GapMultiSelectDropdown";
import { useAddonsState } from "app/contexts/AddonStateContext/AddonStateContext";
import { ADDON_TYPE_NAME_DICT } from "app/contexts/AddonStateContext/const";
import { useNoTouchPurchaseContext } from "app/contexts/NoTouchPurchaseContext/NoTouchPurchaseContext";
import { translate } from "app/filters/i18nFilter";
import type { DisplayAdvertiserPublisherParams } from "app/pages/digital-marketing/ad-intelligence/common/hooks/useDisplayAdvertiserPublisherDataAndParams";

import type { AdIntelligenceController } from "../shared/types/types";
import { getGapTranslationKey, trackExcelDownload } from "../utils/utils";

import { DisplayAdvertisersPublishersFilters } from "./DisplayAdvertisersPublishersFilters";

const FiltersWithGapControls = styled(Flex)`
    align-items: center;
    flex: 1;
`;

const GapInlineControls = styled(Flex)`
    align-items: center;
    gap: 6px;
    margin-left: auto;
    padding-right: 4px;
    ${setFontVariant("body2", colorsPalettes.carbon[500])}
`;

const GapCallout = styled(Flex)`
    align-items: center;
    margin: 8px;
    padding: 8px 12px;
    background: ${colorsPalettes.blue[100]};
    border: 1px solid ${colorsPalettes.blue[200]};
    border-radius: 6px;
    ${setFontVariant("body2", colorsPalettes.blue[500])}
`;

interface IGapProps {
    isActive: boolean;
    isAvailable: boolean;
    perspectives: string[];
    domains: { name: string; color: string }[];
    secondaryDomainsLabel: string;
    onToggle: (value: boolean) => void;
    onPerspectivesChange: (domains: string[]) => void;
}

interface IAdvertisersPublishersTableTopProps {
    controller: AdIntelligenceController;
    resource: AdIntelligenceController;
    isCompareMode: boolean;
    params: DisplayAdvertiserPublisherParams;
    search: string;
    excelDownloadUrl: string;
    onSearchChange: (value: string) => void;
    onFilterChange: (filter: { id?: string }, param: keyof DisplayAdvertiserPublisherParams) => void;
    gap: IGapProps;
    totalCount?: number;
}

export const AdvertisersPublishersTableTop = ({
    controller,
    resource,
    isCompareMode,
    params,
    search,
    excelDownloadUrl,
    onSearchChange,
    onFilterChange,
    gap,
    totalCount = 0,
}: IAdvertisersPublishersTableTopProps) => {
    const { hasAddonsHub } = useAddonsState();
    const { handlePurchaseAddonClick } = useNoTouchPurchaseContext();

    const handleGapToggleClick = () => {
        gap.onToggle(!gap.isActive);
    };

    return (
        <>
            <Table.TableTop borderBottom>
                <Table.TableTop.Filters
                    filters={[
                        {
                            filter: "search",
                            initialValue: search?.[0] ?? "",
                            placeholder: translate(
                                `ad.intelligence.${controller}.${resource}s.table.search.placeholder`,
                            ),
                            onSearchChange: onSearchChange,
                        },
                    ]}
                    factoryFilters={
                        <FiltersWithGapControls>
                            <DisplayAdvertisersPublishersFilters
                                controller={controller}
                                resource={resource}
                                onFilterChange={(value) => {
                                    if (value.industry !== params.category) {
                                        onFilterChange({ id: value.industry }, "category");
                                    }
                                    if (!isCompareMode && value.network !== params.network) {
                                        onFilterChange({ id: value.network }, "network");
                                    }
                                }}
                            />
                            {isCompareMode && gap.isAvailable ? (
                                <GapInlineControls>
                                    <span data-automation={`ad-intel-${controller}-${resource}s-gap-toggle`}>
                                        <OnOffSwitch
                                            isSelected={gap.isActive}
                                            onClick={handleGapToggleClick}
                                        />
                                    </span>
                                    {translate("gap.toggle.sentence.before")}
                                    <GapMultiSelectDropdown
                                        domains={gap.domains}
                                        selected={gap.perspectives}
                                        onChange={gap.onPerspectivesChange}
                                        data-automation={`ad-intel-${controller}-${resource}s-gap-perspective-selector`}
                                    />
                                </GapInlineControls>
                            ) : null}
                        </FiltersWithGapControls>
                    }
                />
                <Table.TableTop.Settings
                    columnPickerType="reorderable"
                    locale={{
                        columnPicker: {
                            mandatoryField: translate("keyword.analysis.table.column.picker.mandatory"),
                        },
                    }}
                    excelExportConfig={{
                        excelDownloadUrl: swSettings.current.resources.IsExcelAllowed ? excelDownloadUrl : null,
                        onExcelDownloaded: trackExcelDownload,
                        hasAddonHub: hasAddonsHub,
                        onCtaClicked: () => handlePurchaseAddonClick(ADDON_TYPE_NAME_DICT.DataCreditsDynamic),
                    }}
                />
            </Table.TableTop>
            {gap.isActive && totalCount > 0 ? (
                <GapCallout>
                    {translate(
                        getGapTranslationKey(controller, "callout", gap.domains.length - gap.perspectives.length > 1),
                        {
                            count: totalCount,
                            primary: gap.perspectives.join(", "),
                            secondary: gap.secondaryDomainsLabel,
                        },
                    )}
                </GapCallout>
            ) : null}
        </>
    );
};
